// Two intake-relevant analysis blocks rendered on the person page:
//
//   - ExpungementSection    - verdict + blockers + eligibility timeline
//                             + LLM-generated clinical strategy memo
//   - ResourcesSection      - referrals filtered for this person's pattern
//
// (UpcomingDatesBanner used to live here too, but its job was subsumed
// by OpenItemsSummary which combines next-date countdown with fines +
// warrants + active items in one unified triage section.)
//
// Both render from data computed server-side (pure modules in
// src/server/lib/) so we keep the client stupid and consistent across views.

import { useState } from "react";
import { renderInlineMarkdown } from "../lib/inlineMarkdown";
import type {
  ExpungementResult,
  Resource,
  ResourceCategory,
} from "../types";

// ─────────────────────────────────────────────────────────────────────────
// Date helpers (kept local to avoid pulling in another util file)
// ─────────────────────────────────────────────────────────────────────────

function formatShortDate(iso: string): string {
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return iso;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

// ─────────────────────────────────────────────────────────────────────────
// EXPUNGEMENT SECTION
// Big verdict tile + blockers + 10-year eligibility timeline. Always carries
// the "heuristic - verify with counsel" disclaimer.
// ─────────────────────────────────────────────────────────────────────────

// Verdict palette anchored against bordeaux (#8C1F2F):
//   ok      → deep forest green   (complement-axis, calming)
//   wait    → deep aged bronze    (warm, NOT acidic yellow)
//   block   → muted plum-burgundy (in the bordeaux family but darker)
//   neutral → deep slate blue     (cool, recedes)
const VERDICT_LOOKS: Record<
  ExpungementResult["verdict"],
  { label: string; tagline: string; bg: string; ink: string; soft: string; tone: "ok" | "wait" | "block" | "neutral" }
> = {
  "likely-eligible": {
    label: "Likely Eligible",
    tagline: "10-year Clean Slate window has cleared.",
    bg: "linear-gradient(155deg, #2E5A3A 0%, #1A3D24 100%)",
    ink: "#fff",
    soft: "rgba(255,255,255,0.86)",
    tone: "ok",
  },
  wait: {
    label: "Wait",
    tagline: "Eligible after the 10-year Clean Slate window closes.",
    bg: "linear-gradient(155deg, #8B6526 0%, #534016 100%)",
    ink: "#fff",
    soft: "rgba(255,255,255,0.86)",
    tone: "wait",
  },
  blocked: {
    label: "Blocked",
    tagline: "Excluded charges or pending cases prevent expungement now.",
    bg: "linear-gradient(155deg, #6E2A36 0%, #3F1620 100%)",
    ink: "#fff",
    soft: "rgba(255,255,255,0.86)",
    tone: "block",
  },
  "no-criminal-records": {
    label: "No 2C Records",
    tagline: "Nothing in 2C: only motor-vehicle cases, if any.",
    bg: "linear-gradient(155deg, #2E4258 0%, #14202E 100%)",
    ink: "#fff",
    soft: "rgba(255,255,255,0.78)",
    tone: "neutral",
  },
  "needs-review": {
    label: "Needs Review",
    tagline: "Insufficient data to compute eligibility automatically.",
    bg: "linear-gradient(155deg, #3A363F 0%, #1F1D22 100%)",
    ink: "#fff",
    soft: "rgba(255,255,255,0.78)",
    tone: "neutral",
  },
};

export function ExpungementSection({
  result,
  incompleteData = false,
  strategy = null,
  records = [],
  onJumpToRecord,
}: {
  result: ExpungementResult;
  /** True when we know the scrape didn't capture every record. Triggers a
   *  stronger disclaimer because excluded charges could be hiding in the
   *  un-pulled rows and would silently flip the verdict. */
  incompleteData?: boolean;
  /** Optional LLM-generated strategy memo (markdown). When present it
   *  renders below the verdict with a fade-out + "read more" toggle. */
  strategy?: string | null;
  /** Full record list - used to enrich the Pending Cases card with date
   *  and court info that isn't in the expungement result itself. */
  records?: Array<{ id: number; data?: unknown }>;
  /** Click handler for jumping from a Pending Case row to its full
   *  record entry in the records section below. Called with the
   *  recordId - caller switches view + scrolls to it. */
  onJumpToRecord?: (recordId: number) => void;
}) {
  const look = VERDICT_LOOKS[result.verdict];
  return (
    <section className="container station">
      {/* Sub-card with title nested inside. No external eyebrow / disclaimer
          - keep the chrome clean. */}
      <div className="sub-card">
        <h3
          className="display text-[color:var(--color-text)] mb-4"
          style={{
            fontSize: "clamp(20px, 2vw, 28px)",
            fontStyle: "italic",
            fontVariationSettings: '"opsz" 60, "SOFT" 60',
            margin: 0,
            marginBottom: 16,
          }}
        >
          Expungement Read
        </h3>
        <p className="ink-note" style={{ margin: "-6px 0 16px", color: "var(--color-text-muted)", fontSize: 14, lineHeight: 1.5 }}>
          Automated legal read from captured public records. Verify against official court records before advising or filing.
        </p>

      {incompleteData && (
        <div
          className="mb-4 rounded-xl border px-4 py-3 text-[14px]"
          style={{
            borderColor: "var(--color-orange-hi)",
            background: "var(--color-orange-soft)",
            color: "var(--color-text)",
          }}
        >
          <strong style={{ color: "var(--color-orange-hi)" }}>⚠ Incomplete data.</strong>
          {" "}Not every record was captured. Excluded charges or pending cases could be in the
          un-pulled rows, re-run the scrape before relying on this verdict.
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,360px)_1fr]">
        {/* Verdict tile */}
        <div
          className="card-elevated dark-tile overflow-hidden p-6"
          style={{ background: look.bg, color: look.ink, border: "none" }}
        >
          <div
            className="eyebrow"
            style={{ color: look.soft, letterSpacing: "0.18em", fontSize: 11 }}
          >
            Verdict
          </div>
          <div
            className="display mt-1 leading-[0.95]"
            style={{
              fontSize: "clamp(36px, 4vw, 56px)",
              color: look.ink,
              fontVariationSettings: '"opsz" 144, "SOFT" 30',
            }}
          >
            {look.label}
          </div>
          <p className="mt-3 text-[15px] lg:text-[18px] lg:leading-[1.45]" style={{ color: look.soft }}>
            {look.tagline}
          </p>

          {/* Eligibility timeline (only meaningful for wait / blocked-with-future) */}
          {result.eligibleOn && (
            <div className="mt-6 border-t pt-4" style={{ borderColor: "rgba(255,255,255,0.2)" }}>
              <div className="eyebrow" style={{ color: look.soft, letterSpacing: "0.18em", fontSize: 10 }}>
                Earliest eligibility
              </div>
              <div className="mt-1 tabular" style={{ fontSize: 22, fontWeight: 700, color: look.ink }}>
                {formatShortDate(result.eligibleOn)}
              </div>
              {result.yearsRemaining != null && (
                <div className="text-[13px] mt-0.5" style={{ color: look.soft }}>
                  {result.yearsRemaining > 0
                    ? `${result.yearsRemaining} year${result.yearsRemaining === 1 ? "" : "s"} from today`
                    : "as of today"}
                </div>
              )}
              {result.latestActivityDate && (
                <div className="text-[12px] mt-2" style={{ color: look.soft }}>
                  Anchor: latest 2C activity {formatShortDate(result.latestActivityDate)}
                </div>
              )}
            </div>
          )}

          {/* Counts strip */}
          {result.counts.total > 0 && (
            <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-2 text-[12px] lg:text-[14px]" style={{ color: look.soft }}>
              <CountRow label="Total cases" value={result.counts.total} ink={look.ink} soft={look.soft} />
              <CountRow label="2C (criminal)" value={result.counts.title2c} ink={look.ink} soft={look.soft} />
              <CountRow label="Title 39 (MV)" value={result.counts.title39} ink={look.ink} soft={look.soft} />
              <CountRow label="Indictable" value={result.counts.indictable} ink={look.ink} soft={look.soft} />
              <CountRow label="Disorderly" value={result.counts.disorderly} ink={look.ink} soft={look.soft} />
              <CountRow label="Pending" value={result.counts.pending} ink={look.ink} soft={look.soft} />
            </div>
          )}
        </div>

        {/* Blockers + notes */}
        <div className="space-y-4">
          {result.blockers.length > 0 && (
            <div className="card p-5">
              <div className="eyebrow text-[color:var(--color-rose-hi)] text-[11px] lg:text-[12px]">Blockers</div>
              <ul className="mt-3 space-y-2 lg:space-y-3">
                {result.blockers.map((b, i) => (
                  <li key={i} className="flex gap-3 text-[15px] lg:text-[18px] lg:leading-[1.5] text-[color:var(--color-text)]">
                    <span style={{ color: "var(--color-rose-hi)" }}>●</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.excludedRecords.length > 0 && (
            <div className="card p-5">
              <div className="eyebrow text-[color:var(--color-text-muted)] text-[11px] lg:text-[12px]">
                Permanently excluded charges (2C:52-2(b))
              </div>
              <ul className="mt-3 space-y-2 lg:space-y-3 text-[14px] lg:text-[16px] lg:leading-[1.5] text-[color:var(--color-text-muted)]">
                {result.excludedRecords.map((r, i) => (
                  <li key={i} className="flex items-baseline gap-3">
                    <span className="mono text-[color:var(--color-text)]">{r.statute}</span>
                    <span>{r.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.pendingRecords.length > 0 && (
            <div className="card p-5">
              <div className="eyebrow text-[color:var(--color-orange-hi)] text-[11px] lg:text-[12px]">Pending cases</div>
              <ul className="mt-3 space-y-1.5">
                {result.pendingRecords.map((r, i) => (
                  <PendingCaseRow
                    key={i}
                    statute={r.statute}
                    status={r.status}
                    recordId={r.recordId}
                    record={records.find((rec) => rec.id === r.recordId)}
                    onJumpToRecord={onJumpToRecord}
                  />
                ))}
              </ul>
            </div>
          )}

          {result.notes.length > 0 && (
            <div className="card p-5">
              <div className="eyebrow text-[color:var(--color-text-muted)] text-[11px] lg:text-[12px]">Notes</div>
              <ul className="mt-3 space-y-2 lg:space-y-3 text-[14px] lg:text-[16px] lg:leading-[1.55] text-[color:var(--color-text-muted)]">
                {result.notes.map((n, i) => (
                  <li key={i}>{n}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Empty-state when no blockers / notes - show a clear "what this means" */}
          {result.blockers.length === 0 && result.notes.length === 0 && (
            <div className="card p-5 text-[14px] lg:text-[16px] lg:leading-[1.55] text-[color:var(--color-text-muted)]">
              {result.verdict === "no-criminal-records"
                ? "No 2C charges on file. Expungement under 2C:52 doesn't apply, any motor-vehicle history is handled separately by the MVC."
                : "No structural blockers found. The verdict above is based purely on the 10-year Clean Slate timer."}
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Case Strategy memo (LLM-generated). Renders as its OWN
          sub-card next to the Expungement Read sub-card above. */}
      {strategy && <StrategyMemo strategy={strategy} />}
    </section>
  );
}

/**
 * Render the LLM-generated clinical expungement strategy with a
 * collapse / fade-out / "Read more" pattern. Markdown is rendered with the
 * existing inline-markdown helper for **bold** + *italic* + dollar/year
 * highlighting. We treat blank lines as paragraph breaks and lines that
 * start with "### " as small section headings.
 */
function StrategyMemo({ strategy }: { strategy: string }) {
  const [expanded, setExpanded] = useState(false);
  const blocks = parseStrategyBlocks(strategy);

  // Show the first 2 blocks by default (verdict + opening reasoning),
  // collapse the rest behind the toggle.
  const visibleCount = expanded ? blocks.length : Math.min(2, blocks.length);
  const visible = blocks.slice(0, visibleCount);
  const hasMore = blocks.length > visibleCount;

  return (
    <div className="sub-card relative overflow-hidden mt-4">
      <h3
        className="display text-[color:var(--color-text)]"
        style={{
          fontSize: "clamp(20px, 2vw, 28px)",
          fontStyle: "italic",
          fontVariationSettings: '"opsz" 60, "SOFT" 60',
          margin: 0,
          marginBottom: 16,
        }}
      >
        Case Strategy
      </h3>
      <p className="ink-note" style={{ margin: "-4px 0 16px", color: "var(--color-text-muted)", fontSize: 14, lineHeight: 1.5 }}>
        Analysis generated from the captured record set. Verify before relying on any legal conclusion.
      </p>
      <div
        className="relative"
      >
        <div className="prose-sm space-y-3 lg:space-y-4 text-[15px] lg:text-[18px] leading-[1.65] lg:leading-[1.7] text-[color:var(--color-text)]">
          {visible.map((b, i) => renderStrategyBlock(b, i))}
        </div>
        {hasMore && !expanded && (
          // Fade-out gradient over the bottom of the visible area
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-24"
            style={{
              background:
                "linear-gradient(180deg, rgba(31,34,38,0) 0%, var(--color-bg-card) 90%)",
            }}
          />
        )}
        {hasMore && (
          <div className="relative flex justify-center" style={{ marginTop: 16, marginBottom: 8 }}>
            <button
              onClick={() => setExpanded(true)}
              className="rounded-lg border px-4 py-2 text-[13px] font-semibold transition hover:bg-[color:var(--color-bg-card-hi)]"
              style={{
                borderColor: "var(--color-line-hi)",
                color: "var(--color-text)",
                background: "var(--color-bg-card-hi)",
              }}
            >
              Read full strategy ↓
            </button>
          </div>
        )}
        {expanded && (
          <div className="relative flex justify-center" style={{ marginTop: 16, marginBottom: 8 }}>
            <button
              onClick={() => setExpanded(false)}
              className="text-[12px] text-[color:var(--color-text-dim)] hover:text-[color:var(--color-text-muted)]"
            >
              Collapse ↑
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

interface StrategyBlock {
  kind: "para" | "heading" | "list";
  text: string;
  items?: string[];
  /** Whether the list is numbered (1. 2. 3.) or bulleted (- * bullets). */
  ordered?: boolean;
}

/** Parse the markdown strategy into typed blocks for predictable rendering. */
function parseStrategyBlocks(md: string): StrategyBlock[] {
  const lines = md.split(/\r?\n/);
  const out: StrategyBlock[] = [];
  let para: string[] = [];
  let list: string[] | null = null;
  let listOrdered = false;

  function flushPara() {
    if (para.length > 0) {
      out.push({ kind: "para", text: para.join(" ").trim() });
      para = [];
    }
  }
  function flushList() {
    if (list && list.length > 0) {
      out.push({ kind: "list", text: "", items: list, ordered: listOrdered });
      list = null;
    }
  }

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flushPara();
      flushList();
      continue;
    }
    if (line.startsWith("### ")) {
      flushPara();
      flushList();
      out.push({ kind: "heading", text: line.slice(4).trim() });
      continue;
    }
    // Numbered list (1. 2. 3.) - preserves order, used for action plans
    const numMatch = line.match(/^(\d+)\.\s+(.+)$/);
    if (numMatch) {
      flushPara();
      if (!list) {
        list = [];
        listOrdered = true;
      } else if (!listOrdered) {
        // Switching from bulleted to numbered - close the previous list
        flushList();
        list = [];
        listOrdered = true;
      }
      list.push(numMatch[2]!);
      continue;
    }
    // Bulleted list (- or *)
    const bulletMatch = line.match(/^[-*]\s+(.+)$/);
    if (bulletMatch) {
      flushPara();
      if (!list) {
        list = [];
        listOrdered = false;
      } else if (listOrdered) {
        flushList();
        list = [];
        listOrdered = false;
      }
      list.push(bulletMatch[1]!);
      continue;
    }
    flushList();
    para.push(line);
  }
  flushPara();
  flushList();
  return out;
}

function renderStrategyBlock(b: StrategyBlock, key: number) {
  if (b.kind === "heading") {
    return (
      <h4
        key={key}
        className="mt-4 lg:mt-6 text-[color:var(--color-text)]"
        style={{
          fontFamily: "var(--font-display)",
          // clamp scales with viewport so mobile (16) and desktop (21) both
          // feel right without a media-query breakpoint.
          fontSize: "clamp(16px, 1.5vw, 21px)",
          fontWeight: 700,
          letterSpacing: "-0.01em",
        }}
      >
        {b.text}
      </h4>
    );
  }
  if (b.kind === "list") {
    if (b.ordered) {
      return (
        <ol key={key} className="ml-5 list-decimal space-y-1.5">
          {b.items!.map((it, i) => (
            <li key={i} className="text-[color:var(--color-text)] pl-1" style={{ lineHeight: 1.6 }}>
              {renderInlineMarkdown(it)}
            </li>
          ))}
        </ol>
      );
    }
    return (
      <ul key={key} className="ml-5 list-disc space-y-1.5">
        {b.items!.map((it, i) => (
          <li key={i} className="text-[color:var(--color-text)] pl-1" style={{ lineHeight: 1.6 }}>
            {renderInlineMarkdown(it)}
          </li>
        ))}
      </ul>
    );
  }
  return (
    <p
      key={key}
      className="text-[color:var(--color-text)]"
      style={{
        fontFamily: "var(--font-serif)",
        // Slight italic + larger size + serifs make the prose feel like
        // editorial counsel guidance instead of a UI control. Lists stay
        // in Inter for scannability. clamp() scales mobile→desktop without
        // a breakpoint flip - 16.5 on phones, ~20 on a wide monitor.
        fontStyle: "italic",
        fontSize: "clamp(16.5px, 1.55vw, 20.5px)",
        lineHeight: 1.62,
        fontVariationSettings: '"opsz" 36, "SOFT" 80',
      }}
    >
      {renderInlineMarkdown(b.text)}
    </p>
  );
}

function CountRow({ label, value, ink, soft }: { label: string; value: number; ink: string; soft: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2 border-b pb-1" style={{ borderColor: "rgba(255,255,255,0.12)" }}>
      <span style={{ color: soft }}>{label}</span>
      <span className="tabular" style={{ color: ink, fontWeight: 600 }}>{value}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// RESOURCES SECTION
// Three-column grid of referral cards. Each card shows the org name,
// a short blurb, the "why we matched it" line, and contact links.
// ─────────────────────────────────────────────────────────────────────────

const CATEGORY_LOOK: Record<ResourceCategory, { label: string; color: string }> = {
  njrc:       { label: "NJRC",          color: "var(--color-sage-hi)" },
  substance:  { label: "Substance use", color: "var(--color-blue-hi)" },
  dwi:        { label: "DWI",           color: "var(--color-orange-hi)" },
  mental:     { label: "Mental health", color: "var(--color-blue-hi)" },
  violence:   { label: "Violence / DV", color: "var(--color-rose-hi)" },
  weapons:    { label: "Weapons",       color: "var(--color-rose-hi)" },
  employment: { label: "Employment",    color: "var(--color-sage-hi)" },
  housing:    { label: "Housing",       color: "var(--color-gold-hi)" },
  veterans:   { label: "Veterans",      color: "var(--color-blue-hi)" },
  legal:      { label: "Legal",         color: "var(--color-text-muted)" },
  youth:      { label: "Youth",         color: "var(--color-sage-hi)" },
};

// ─────────────────────────────────────────────────────────────────────────
// ResourcesSection - three-state fan-out deck.
//
// State machine:
//    stacked  → fanned   → focused
//
// Stacked: cards collapsed at center, slight rotation, single "deck."
// Fanned:  cards spread in an arc; tap any individual card → focused.
// Focused: tapped card grows to hero size, others dim + tuck behind.
//
// Desktop (lg+): two pieces of giant italic Fraunces flanking the deck -
// "Resources." on the left, "Tap a card." on the right - fade away
// once the deck is opened. (They'd compete with the spread cards.)
// ─────────────────────────────────────────────────────────────────────────

export function ResourcesSection({ resources }: { resources: Resource[] }) {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState<number | null>(null);

  if (!resources || resources.length === 0) return null;
  const N = resources.length;

  return (
    <section className="container station">
      <div className="sub-card">
        <h3
          className="display text-[color:var(--color-text)]"
          style={{
            fontSize: "clamp(20px, 2vw, 28px)",
            fontStyle: "italic",
            fontVariationSettings: '"opsz" 60, "SOFT" 60',
            margin: 0,
            marginBottom: 16,
          }}
        >
          Suggested Resources
        </h3>

      <div
        className="suggested-resources-deck"
        onClick={() => {
          if (focused !== null) setFocused(null);
          else setOpen(!open);
        }}
        style={{
          position: "relative",
          minHeight: 460,
          borderRadius: 12,
          background: "color-mix(in oklab, var(--color-text) 3%, transparent)",
          border: "1px solid var(--color-line)",
          padding: "30px 0 50px",
          cursor: "pointer",
          overflow: "hidden",
        }}
      >
        {/* Backdrop dim while a card is focused */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            opacity: focused !== null ? 1 : 0,
            transition: "opacity 0.35s ease",
            pointerEvents: "none",
          }}
        />

        {/* The deck itself */}
        <div
          style={{
            position: "relative",
            width: "100%",
            height: 380,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {resources.map((r, i) => {
            const center = (N - 1) / 2;
            const offset = i - center;
            const isFocused = focused === i;
            const someoneFocused = focused !== null;

            // Resolve transform / size / depth across the three states.
            let transform: string;
            let width = 220;
            let height = 280;
            let opacity = 1;
            let zIndex: number;
            let transformOrigin = "50% 100%";

            if (isFocused) {
              transform = "translate(0px, -8px) rotate(0deg)";
              transformOrigin = "50% 50%";
              width = 340;
              height = 380;
              zIndex = 100;
            } else if (someoneFocused) {
              transform = `translate(${offset * 32}px, 110px) rotate(${offset * 4}deg) scale(0.6)`;
              opacity = 0.35;
              zIndex = i;
            } else if (open) {
              // Fanned arc - scale x-spread with N so 3 vs 9 cards both look
              // intentional rather than crowded or sparse.
              const fanX = (offset / Math.max(center, 1)) * Math.min(N * 38, 280);
              transform = `translate(${fanX}px, ${Math.abs(offset) * 6}px) rotate(${offset * Math.min(11, 60 / N)}deg)`;
              zIndex = i;
            } else {
              transform = `translate(${offset * 4}px, ${Math.abs(offset) * 0.5}px) rotate(${offset * 1.5}deg)`;
              zIndex = N - i;
            }

            return (
              <FannedResourceCard
                key={r.id}
                resource={r}
                isFocused={isFocused}
                width={width}
                height={height}
                opacity={opacity}
                transform={transform}
                transformOrigin={transformOrigin}
                zIndex={zIndex}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isFocused) setFocused(null);
                  else if (open) setFocused(i);
                  else setOpen(true);
                }}
              />
            );
          })}
        </div>

        {/* Caption strip at the bottom - context-sensitive guidance */}
        <div
          className="absolute bottom-3 left-0 right-0 text-center text-[11px] italic"
          style={{
            color: "var(--color-text-dim)",
            fontFamily: "var(--font-serif)",
            pointerEvents: "none",
          }}
        >
          {focused !== null
            ? "tap card or backdrop to close"
            : open
            ? "tap a card for details · click outside to re-stack"
            : `click anywhere to fan out · ${N} resource${N === 1 ? "" : "s"}`}
        </div>
      </div>
      </div>
    </section>
  );
}

// - Single fan-able card --------------------------------------------------
//
// Mirrors the look of the previous flat ResourceCard (category-tinted
// border, eyebrow, name, blurb, phone, url) but is positioned + sized
// by the parent for the fan animation. When focused, expands to show
// blurb + why + clickable phone + clickable website link.

function FannedResourceCard({
  resource,
  isFocused,
  width,
  height,
  opacity,
  transform,
  transformOrigin,
  zIndex,
  onClick,
}: {
  resource: Resource;
  isFocused: boolean;
  width: number;
  height: number;
  opacity: number;
  transform: string;
  transformOrigin: string;
  zIndex: number;
  onClick: (e: React.MouseEvent) => void;
}) {
  const look = CATEGORY_LOOK[resource.category];
  const accent = look.color;

  return (
    <div
      onClick={onClick}
      style={{
        position: "absolute",
        width,
        height,
        background:
          "linear-gradient(160deg, var(--color-surface-2, #1f1c18) 0%, var(--color-surface, #14110e) 100%)",
        border: isFocused
          ? `1px solid color-mix(in oklab, ${accent} 55%, transparent)`
          : `1px solid color-mix(in oklab, ${accent} 28%, transparent)`,
        borderRadius: 14,
        padding: isFocused ? "26px 24px" : "20px 18px",
        boxShadow: isFocused
          ? "0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04)"
          : "0 12px 32px rgba(0,0,0,0.45)",
        transform,
        transformOrigin,
        opacity,
        transition:
          "transform 0.55s cubic-bezier(0.2, 0.8, 0.2, 1), width 0.45s cubic-bezier(0.2, 0.8, 0.2, 1), height 0.45s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.35s ease, padding 0.4s ease, box-shadow 0.4s ease, border-color 0.3s ease",
        zIndex,
        cursor: "pointer",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div className="flex items-baseline justify-between gap-3">
        <span
          className="eyebrow text-[10px]"
          style={{ color: accent, letterSpacing: "0.18em" }}
        >
          {look.label}
        </span>
        {resource.why && !isFocused && (
          <span
            className="text-[10px] italic"
            style={{ color: "var(--color-text-dim)" }}
            title={resource.why}
          >
            matched
          </span>
        )}
      </div>

      <div
        className="display mt-2"
        style={{
          fontSize: isFocused ? "clamp(20px, 1.8vw, 24px)" : 17,
          color: "var(--color-text)",
          fontVariationSettings: '"opsz" 36, "SOFT" 50',
          lineHeight: 1.2,
          transition: "font-size 0.4s ease",
        }}
      >
        {resource.name}
      </div>

      <p
        className="mt-2 leading-snug"
        style={{
          fontSize: isFocused ? 14 : 12.5,
          color: "var(--color-text-muted)",
          transition: "font-size 0.4s ease",
          // When stacked/fanned, hard cap the blurb to a few lines so
          // long copy doesn't blow the card height.
          ...(isFocused
            ? {}
            : {
                display: "-webkit-box",
                WebkitLineClamp: 4,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }),
        }}
      >
        {resource.blurb}
      </p>

      {isFocused && resource.why && (
        <p
          className="mt-2 text-[12px] italic"
          style={{
            color: "var(--color-text-dim)",
            animation: "resource-fade-late 0.4s 0.12s both",
          }}
        >
          → {resource.why}
        </p>
      )}

      <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 pt-3 text-[13px]">
        {resource.phone && (
          <a
            href={`tel:${resource.phone.replace(/[^0-9+]/g, "")}`}
            onClick={(e) => e.stopPropagation()}
            className="tabular hover:text-[color:var(--color-orange-hi)]"
            style={{ color: "var(--color-text)" }}
          >
            ☎ {resource.phone}
          </a>
        )}
        {isFocused && resource.url && (
          <a
            href={resource.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="hover:text-[color:var(--color-orange-hi)] underline-offset-4 hover:underline"
            style={{
              color: "var(--color-blue-hi)",
              animation: "resource-fade-late 0.4s 0.18s both",
            }}
          >
            website ↗
          </a>
        )}
      </div>

      {isFocused && (
        <div
          className="absolute bottom-3 left-0 right-0 text-center text-[9px] uppercase tracking-[0.22em]"
          style={{
            color: "rgba(255,255,255,0.35)",
            fontFamily: "var(--font-mono)",
            animation: "resource-fade-late 0.45s 0.25s both",
            pointerEvents: "none",
          }}
        >
          tap to close
        </div>
      )}

      <style>{`
        @keyframes resource-fade-late {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// - Single Pending Case row - enriches the bare {statute, status} from
//   the expungement result with the matching record's date + court (looked
//   up by recordId), and is clickable: tapping it scrolls the matching
//   card down in the records section into view + highlights it briefly. //

function PendingCaseRow({
  statute,
  status,
  recordId,
  record,
  onJumpToRecord,
}: {
  statute: string;
  status: string;
  recordId: number;
  record: { id: number; data?: unknown } | undefined;
  onJumpToRecord?: (recordId: number) => void;
}): JSX.Element {
  const summary = (record?.data as { summary?: Record<string, string | string[]> } | undefined)?.summary;
  const pick = (k: string): string => {
    const v = summary?.[k];
    if (!v) return "";
    return Array.isArray(v) ? v[v.length - 1] ?? "" : v;
  };
  const dateRaw =
    pick("Court Date/Time") ||
    pick("Court Date") ||
    pick("Filing Date") ||
    pick("Issue Date") ||
    pick("Offense Date");
  const dateMatch = dateRaw.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  const date = dateMatch ? `${dateMatch[1]}/${dateMatch[2]}/${dateMatch[3]}` : "";
  const courtRaw = pick("Court");
  const court = courtRaw
    ? courtRaw
        .replace(/^\d+\s*-\s*/, "")
        .replace(/\s+(?:MUNICIPAL|MUNI|MUN)\s+(?:COURT|CT)\s*$/i, " Municipal")
        .toLowerCase()
        .split(/\s+/)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ")
    : "";
  const offense = pick("Offense Description") || pick("Offense") || "";

  const isClickable = Boolean(onJumpToRecord);
  const Inner = (
    <>
      <span
        className="mono"
        style={{ color: "var(--color-text)", fontSize: 13, fontWeight: 600, flexShrink: 0 }}
      >
        {statute}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          className="text-[14px] truncate"
          style={{ color: "var(--color-text)", fontWeight: 500 }}
          title={offense}
        >
          {offense || `Status: ${status}`}
        </div>
        <div
          className="mt-0.5 text-[12px] truncate"
          style={{ color: "var(--color-text-muted)", fontFamily: "var(--font-mono)" }}
        >
          {[date, court, status].filter(Boolean).join(" · ")}
        </div>
      </div>
      {isClickable && (
        <span
          aria-hidden="true"
          className="text-[color:var(--color-text-dim)] group-hover:text-[color:var(--color-orange-hi)] transition-colors"
          style={{ fontSize: 14, flexShrink: 0 }}
        >
          ↓
        </span>
      )}
    </>
  );

  return (
    <li>
      {isClickable ? (
        <button
          type="button"
          onClick={() => onJumpToRecord!(recordId)}
          title="Jump to this record's full card below"
          className="group flex w-full items-baseline gap-3 rounded-md border border-[color:var(--color-line)] px-3 py-2 text-left transition hover:border-[color:var(--color-orange-hi)] hover:bg-[color:var(--color-orange-soft)]"
          style={{ background: "color-mix(in oklab, var(--color-text) 3%, transparent)", cursor: "pointer" }}
        >
          {Inner}
        </button>
      ) : (
        <div
          className="flex w-full items-baseline gap-3 rounded-md border border-transparent px-3 py-2"
          style={{ background: "rgba(255,255,255,0.025)" }}
        >
          {Inner}
        </div>
      )}
    </li>
  );
}
